<template>
  <BbInfiniteScroll :handler="loadAssets" :distance="240" :disabled="disableInfiniteScroll" class="media-assets">
    <BbMediaCard
      v-for="image in images"
      :assetId="image.documentNumber"
      :key="image.documentNumber || image.name"
      :id="image.documentNumber"
      :name="image.name"
      :url="image.url"
      status="approved"
      statusText="Released"
      :version="image.version || 1"
      @click="selectImage(image.originalUrl)"
    />
    <template v-if="disableInfiniteScroll && hasMoreImages">
      <BbMediaCardSkeleton v-for="index in 4" size="large" :key="index" />
    </template>
  </BbInfiniteScroll>
</template>

<script>
import { useWesley } from '@anthill-modules/wesley-components';
import { useAuth0 } from '@auth0/auth0-vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { FILTER_TYPE } from '~/constants/documents';

import useDocuments from '~/composables/useDocuments';
import useFormFilters from '~/composables/useFormFilters';
import useModals from '~/composables/useModals';
import useDocumentEditor from '~/composables/useDocumentEditor';

export default {
  props: {
    mediaAssets: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    const { t } = useI18n();
    const { mediaPanelOpened, setMediaPanelOpened } = useModals();
    const { sendMessage: sendMessageToDocument, asyncSendMessage: sendAsyncMessageToDocument } = useWesley();
    const { formFilters, searchQuery, setSearchQuery } = useFormFilters();
    const { user } = useAuth0();
    const { fetchMediaImages, getAuthoredDocumentImportedThumbs, setTotalMediaImages, totalMediaImages } =
      useDocuments();
    const { saveHtmlDocumentContentToDb, renderCitationTooltipsInIframe } = useDocumentEditor();

    const hasMoreImages = ref(false);
    const isLoading = ref(false);
    const offset = ref(0);
    const limit = 20;
    const images = ref([]);

    const totalEntries = computed(() => totalMediaImages.value);
    const disableInfiniteScroll = computed(() => isLoading.value || !hasMoreImages.value);

    const prepareDataForRequest = (type) => {
      const filters = transformFiltersByType(formFilters.value, type); // only current type
      const search = searchQuery.value || '';
      hasMoreImages.value = offset.value + limit < totalEntries.value || offset.value === 0;
      isLoading.value = true;

      return { filters, search };
    };

    const updateDataAfterResponse = async (hits) => {
      if (hits.length) {
        const imagesUrl = await getAuthoredDocumentImportedThumbs(hits.map((image) => image.externalId));
        const newImages = hits.map((hit) => ({
          ...hit,
          url:
            imagesUrl.find((image) => image.id === hit.externalId && image.variant === 'medium')?.url ||
            imagesUrl.find((image) => image.id === hit.externalId)?.url ||
            '',
          originalUrl: imagesUrl.find((image) => image.id === hit.externalId)?.url || '',
        }));

        images.value = [...images.value, ...newImages];
      }

      offset.value = offset.value + limit;
      isLoading.value = false;
    };

    const transformFiltersByType = (formFilters, type) => {
      const typeFilters = formFilters[type] || {};
      const result = [];

      for (const [key, values] of Object.entries(typeFilters)) {
        if (values && values.length > 0) {
          result.push({
            key,
            value: Array.isArray(values) ? values : [values],
          });
        }
      }

      return result;
    };

    const loadAssets = async () => {
      const { filters, search } = prepareDataForRequest(FILTER_TYPE.media);

      const orgId = user.value.org_id.split('_')[1];

      // call api with limit, offset, filters, searchQuery
      const response = await fetchMediaImages({
        query: search,
        orgId,
        filter: filters,
        from: offset.value,
        size: limit,
      });
      const hits = response.hits?.hits || [];
      setTotalMediaImages(response.hits?.total?.value);

      await updateDataAfterResponse(hits);
    };

    const selectImage = async (img) => {
      const imageData = images.value.find((image) => image.originalUrl === img);
      let imageUrl = img;

      if (imageData?.externalId) {
        try {
          const freshImages = await getAuthoredDocumentImportedThumbs([imageData.externalId]);
          const freshImage = freshImages.find(
            (image) => image.id === imageData.externalId && image.variant === 'original',
          );
          if (freshImage?.url) {
            imageUrl = freshImage.url;
          }
        } catch (error) {
          console.warn('Failed to refresh image URL, using original:', error);
        }
      }

      await sendAsyncMessageToDocument({
        method: 'updateAttribute',
        params: {
          query: mediaPanelOpened.value.cssPath,
          value: imageUrl,
          attr: 'src',
        },
      });
      setMediaPanelOpened({ opened: false });
      setTimeout(() => {
        renderCitationTooltipsInIframe();
        setSearchQuery('');
        sendMessageToDocument({ method: 'deselectElement' });
        saveHtmlDocumentContentToDb();
      }, 1000);
    };

    watch(
      [formFilters, searchQuery],
      () => {
        offset.value = 0;
        images.value = [];
        loadAssets();
      },
      { deep: true, immediate: true },
    );

    return { t, loadAssets, selectImage, disableInfiniteScroll, images, hasMoreImages };
  },
};
</script>

<style lang="scss" scoped>
.media-assets {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  grid-gap: $bb-spacing-4 $bb-spacing-5;

  @media all and (min-width: 1440px) {
    grid-gap: $bb-spacing-4 $bb-spacing-5;
  }

  @media all and (min-width: 1600px) {
    grid-gap: $bb-spacing-5 $bb-spacing-7;
  }

  @media all and (min-width: 1920px) {
    grid-gap: $bb-spacing-5 $bb-spacing-9;
  }

  @media all and (min-width: 2560px) {
    grid-gap: $bb-spacing-7 $bb-spacing-11;
  }
}
</style>
